import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Settings2, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/support`;

const CATEGORY_LABELS = {
  cancel_refund: 'Cancel / Refund',
  date_change: 'Date Change',
  name_correction: 'Name Correction',
  seat_selection: 'Seat Selection',
  add_baggage: 'Add Baggage',
  add_meal: 'Add Meal',
  special_request: 'Special Request',
  gst_invoice: 'GST / Invoice',
  payment_wallet: 'Payment / Wallet',
  schedule_change: 'Schedule Change',
  failed_booking: 'Failed Booking',
  reprice: 'Reprice',
  supplier_escalation: 'Supplier Escalation',
  group_booking: 'Group Booking',
  seat_block: 'Seat Block',
  general_inquiry: 'General Inquiry',
  other: 'Other',
};

export default function SupportAssignmentSettings({ canEdit }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');
  const [executives, setExecutives] = useState([]);
  const [categories, setCategories] = useState([]);
  const [byCategory, setByCategory] = useState({});
  const [roundRobin, setRoundRobin] = useState(false);

  const load = useCallback(async () => {
    if (!canEdit) return;
    setLoading(true);
    setErr('');
    try {
      const r = await axios.get(`${API}/assignment-config`, { withCredentials: true });
      const d = r.data || {};
      setExecutives(d.assignable_executives || []);
      setCategories(d.categories || Object.keys(CATEGORY_LABELS));
      setByCategory(d.assignee_by_category || {});
      setRoundRobin(!!d.round_robin_enabled);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not load assignment settings');
    } finally {
      setLoading(false);
    }
  }, [canEdit]);

  useEffect(() => {
    if (open && canEdit) load();
  }, [open, canEdit, load]);

  const save = async () => {
    setSaving(true);
    setErr('');
    setOk('');
    try {
      await axios.put(
        `${API}/assignment-config`,
        { assignee_by_category: byCategory, round_robin_enabled: roundRobin },
        { withCredentials: true },
      );
      setOk('Assignment rules saved.');
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!canEdit) return null;

  return (
    <div className="mb-4 bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="support-assignment-settings">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50"
      >
        <span className="text-sm font-semibold text-[#1B375B] flex items-center gap-2">
          <Settings2 className="w-4 h-4 text-[#659A9B]" />
          Auto-assignment rules
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
      </button>
      {open && (
        <div className="px-4 pb-4 border-t border-gray-100 space-y-3">
          <p className="text-xs text-gray-600 pt-2">
            Set a default ops executive per category, or leave blank to use round-robin (when enabled).
            Only active Ops Executives from Staff Management appear below.
          </p>
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : (
            <>
              <label className="flex items-center gap-2 text-sm text-gray-800 cursor-pointer">
                <input
                  type="checkbox"
                  checked={roundRobin}
                  onChange={(e) => setRoundRobin(e.target.checked)}
                  data-testid="support-round-robin-toggle"
                />
                Round-robin among ops executives (for categories without a fixed assignee)
              </label>
              {executives.length === 0 && (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  No ops executives found. Add staff with role Ops Executive first.
                </p>
              )}
              <div className="max-h-[320px] overflow-y-auto border border-gray-100 rounded-lg divide-y divide-gray-100">
                {categories.map((cat) => (
                  <div key={cat} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm">
                    <span className="w-40 flex-shrink-0 text-gray-700 font-medium">
                      {CATEGORY_LABELS[cat] || cat}
                    </span>
                    <select
                      value={byCategory[cat] || ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        setByCategory((m) => {
                          const next = { ...m };
                          if (v) next[cat] = v;
                          else delete next[cat];
                          return next;
                        });
                      }}
                      className="flex-1 min-w-[200px] text-sm border border-gray-300 rounded-lg px-2 py-1"
                      data-testid={`assign-cat-${cat}`}
                    >
                      <option value="">— Default (round-robin if enabled) —</option>
                      {executives.map((ex) => (
                        <option key={ex.id} value={ex.id}>{ex.name || ex.email}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
              {err && <p className="text-xs text-red-700">{err}</p>}
              {ok && <p className="text-xs text-emerald-700">{ok}</p>}
              <button
                type="button"
                onClick={save}
                disabled={saving}
                className="text-sm px-4 py-2 rounded-lg bg-[#1B375B] text-white font-semibold hover:bg-[#0f2742] disabled:opacity-50"
                data-testid="support-assignment-save"
              >
                {saving ? 'Saving…' : 'Save rules'}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
